import {ConsoleLogger} from 'aws-amplify/utils';
import localforage from 'localforage';

const logger = new ConsoleLogger('safeLocalforage');

/**
 * Enveloppes de localforage qui ne rejettent jamais.
 *
 * Safari iOS coupe la connexion IndexedDB sous nos pieds — au retour de l'onglet
 * au premier plan, typiquement — et les opérations suivantes rejettent avec
 * « UnknownError: Attempt to … from database without an in-progress
 * transaction » (PERFECTPOST-WEB-10G, PERFECTPOST-WEB-10H). C'est un défaut de
 * WebKit, pas le nôtre, et rien de ce qu'on stocke là n'est indispensable : des
 * préférences d'affichage, qui retombent sur leurs valeurs par défaut.
 *
 * D'où un simple avertissement en console, sans `captureException` : remonter
 * ces échecs dans Sentry n'apprendrait rien qu'on ne sache déjà.
 */
const warn = (operation: string, error: unknown) => {
  logger.warn(`localforage.${operation} a échoué`, error instanceof Error ? error.message : String(error));
};

/** Lit une clé ; `undefined` si elle est absente ou si le stockage refuse. */
export async function safeGetItem<T>(key: string): Promise<T | undefined> {
  try {
    // localforage rend `null` pour une clé absente : on le convertit ici, à la frontière.
    return (await localforage.getItem<T>(key)) ?? undefined;
  } catch (error) {
    warn('getItem', error);
    return undefined;
  }
}

export async function safeSetItem<T>(key: string, value: T): Promise<void> {
  try {
    await localforage.setItem(key, value);
  } catch (error) {
    warn('setItem', error);
  }
}

export async function safeRemoveItem(key: string): Promise<void> {
  try {
    await localforage.removeItem(key);
  } catch (error) {
    warn('removeItem', error);
  }
}

export async function safeClear(): Promise<void> {
  try {
    await localforage.clear();
  } catch (error) {
    warn('clear', error);
  }
}
